import React, { useState, useEffect } from 'react';
import "../Styles/Upcoming_tour_content.css";
import api from '../Api.js';
import dayjs from 'dayjs';

// 🗺️ Itinerary Modal Component with Day-wise Tracking
const ItineraryModal = ({ booking, onClose }) => {
  const [expandedDay, setExpandedDay] = useState(null);

  if (!booking || !booking.itinerary || booking.itinerary.length === 0) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📅</div>
            <h3 style={{ color: '#666' }}>No Itinerary Available</h3>
            <p style={{ color: '#999' }}>Itinerary details for this tour are not yet added.</p>
          </div>
        </div>
      </div>
    );
  }

  const toggleDay = (index) => {
    setExpandedDay(expandedDay === index ? null : index);
  };

  // Get today's date (without time for comparison)
  const today = dayjs().format('DD-MM-YYYY');
  
  // Determine the status of each day
  const getDayStatus = (day) => {
    const dayDate = day.date;
    
    if (!dayDate) {
      return 'upcoming';
    }
    
    const dayMoment = dayjs(dayDate, 'DD-MM-YYYY');
    const todayMoment = dayjs(today, 'DD-MM-YYYY');
    
    if (dayMoment.isBefore(todayMoment, 'day')) {
      return 'completed';
    } else if (dayMoment.isSame(todayMoment, 'day')) {
      return 'current';
    } else {
      return 'upcoming';
    }
  };

  // Get colors based on day status with AF7A2C gold theme
  const getDayColors = (day) => {
    const status = getDayStatus(day);
    
    switch(status) {
      case 'completed':
        return {
          dotBg: '#2E7D32',
          dotBorder: '#2E7D32',
          lineColor: '#2E7D32',
          cardBg: 'linear-gradient(135deg, #F1F8E9 0%, #E8F5E9 100%)',
          cardBorder: '#4CAF50',
          borderLeftColor: '#4CAF50',
          titleColor: '#1B5E20',
          statusText: '✅ Completed',
          statusColor: '#2E7D32',
          statusBg: '#E8F5E9',
          dotShadow: '0 0 0 4px rgba(76, 175, 80, 0.3)',
          icon: '🌟'
        };
      case 'current':
        return {
          dotBg: '#F57C00',
          dotBorder: '#F57C00',
          lineColor: '#F57C00',
          cardBg: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
          cardBorder: '#FFA726',
          borderLeftColor: '#AF7A2C',
          titleColor: '#E65100',
          statusText: '📍 Today',
          statusColor: '#AF7A2C',
          statusBg: '#FFF8E1',
          dotShadow: '0 0 0 8px rgba(175, 122, 44, 0.3)',
          icon: '🔥'
        };
      default:
        return {
          dotBg: '#BDBDBD',
          dotBorder: '#BDBDBD',
          lineColor: '#BDBDBD',
          cardBg: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%)',
          cardBorder: '#E0E0E0',
          borderLeftColor: '#E0E0E0',
          titleColor: '#757575',
          statusText: '⏳ Upcoming',
          statusColor: '#9E9E9E',
          statusBg: '#F5F5F5',
          dotShadow: '0 0 0 4px rgba(189, 189, 189, 0.2)',
          icon: '✨'
        };
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header with AF7A2C Gold Theme */}
        <button onClick={onClose} style={closeBtnStyle}>✕</button>
        
        <div style={{
          ...modalHeaderStyle,
          background: `linear-gradient(135deg, ${'#AF7A2C'} 0%, ${'#C8963E'} 50%, ${'#D4A84E'} 100%)`,
          margin: '-32px -28px 24px -28px',
          padding: '32px 28px 24px 28px',
          borderBottom: '3px solid #8B6914',
          borderRadius: '16px 16px 0 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative golden shimmer overlay */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle at 70% 30%, rgba(255,215,0,0.1) 0%, transparent 60%)',
            pointerEvents: 'none'
          }}></div>
          
          <div style={modalTitleStyle}>
            <span style={{ fontSize: '32px', marginRight: '12px' }}>🗺️</span>
            <div>
              <h2 style={{ 
                margin: 0, 
                fontSize: '24px', 
                color: '#FFFFFF',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                Tour Itinerary
              </h2>
              <p style={{ 
                margin: '4px 0 0', 
                color: '#FFF8E1', 
                fontSize: '14px',
                opacity: 0.9,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                {booking.tour?.title}
              </p>
            </div>
          </div>
          <div style={{
            ...badgeStyle,
            background: `linear-gradient(135deg, ${'#8B6914'} 0%, ${'#AF7A2C'} 100%)`,
            color: '#fff',
            boxShadow: '0 4px 12px rgba(175, 122, 44, 0.4)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <span style={{ fontWeight: '600' }}>{booking.itinerary.length} Days</span>
          </div>
        </div>

        {/* Tracking Legend with AF7A2C Gold Theme */}
        <div style={{
          display: 'flex',
          gap: '20px',
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF8E1 100%)',
          borderRadius: '10px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          border: `1px solid ${'#AF7A2C'}33`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#2E7D32' }}></span>
            <span style={{ fontSize: '13px', color: '#333' }}>Completed</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#AF7A2C' }}></span>
            <span style={{ fontSize: '13px', color: '#333' }}>Today</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#BDBDBD' }}></span>
            <span style={{ fontSize: '13px', color: '#333' }}>Upcoming</span>
          </div>
        </div>

        {/* Itinerary Timeline */}
        <div style={timelineContainerStyle}>
          {booking.itinerary.map((day, index) => {
            const dayStatus = getDayStatus(day);
            const colors = getDayColors(day);
            const isLastItem = index === booking.itinerary.length - 1;
            const nextDayStatus = index < booking.itinerary.length - 1 ? getDayStatus(booking.itinerary[index + 1]) : null;
            
            const shouldLineBeActive = dayStatus === 'completed' || dayStatus === 'current';
            const nextShouldLineBeActive = nextDayStatus === 'completed' || nextDayStatus === 'current';
            
            return (
              <div key={index} style={timelineItemStyle}>
                {/* Day Circle with Progress Line */}
                <div style={timelineLeftStyle}>
                  <div style={timelineLineContainerStyle}>
                    {/* Line above dot */}
                    {index > 0 && (
                      <div style={{
                        ...timelineLineStyle,
                        background: shouldLineBeActive 
                          ? `linear-gradient(to bottom, ${colors.lineColor}, ${colors.lineColor})`
                          : 'linear-gradient(to bottom, #E0E0E0, #E0E0E0)',
                        opacity: shouldLineBeActive ? 1 : 0.4,
                        minHeight: '20px',
                      }}></div>
                    )}
                    
                    {/* Dot with AF7A2C Gold Theme Effects */}
                    <div style={{
                      ...timelineDotStyle,
                      background: dayStatus === 'current' 
                        ? `radial-gradient(circle at 35% 35%, ${'#AF7A2C'}, ${'#8B6914'})`
                        : `radial-gradient(circle at 35% 35%, ${colors.dotBg}, ${colors.dotBg}dd)`,
                      border: `3px solid ${dayStatus === 'current' ? '#AF7A2C' : colors.dotBorder}`,
                      boxShadow: dayStatus === 'current' 
                        ? '0 0 0 8px rgba(175, 122, 44, 0.3)' 
                        : colors.dotShadow,
                      animation: dayStatus === 'current' ? 'pulse-gold 1.5s ease-in-out infinite' : 'none',
                    }}>
                      <span style={{ 
                        color: '#fff', 
                        fontSize: '13px', 
                        fontWeight: 'bold',
                        position: 'relative',
                        zIndex: 2,
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                      }}>
                        {day.day || index + 1}
                      </span>
                    </div>

                    {/* Line below dot */}
                    {!isLastItem && (
                      <div style={{
                        ...timelineLineStyle,
                        background: nextShouldLineBeActive 
                          ? `linear-gradient(to bottom, ${colors.lineColor}, ${colors.lineColor})`
                          : 'linear-gradient(to bottom, #E0E0E0, #E0E0E0)',
                        opacity: nextShouldLineBeActive ? 1 : 0.4,
                        minHeight: '20px',
                      }}></div>
                    )}
                  </div>
                </div>

                {/* Day Content - Beautiful Gold Themed Cards */}
                <div style={timelineContentStyle}>
                  <div 
                    style={{
                      ...dayCardStyle,
                      background: colors.cardBg,
                      borderColor: colors.cardBorder,
                      borderLeft: `4px solid ${dayStatus === 'current' ? '#AF7A2C' : colors.borderLeftColor}`,
                      boxShadow: dayStatus === 'current' 
                        ? '0 4px 20px rgba(175, 122, 44, 0.15)' 
                        : '0 2px 8px rgba(0,0,0,0.04)',
                      transform: expandedDay === index ? 'scale(1.01)' : 'scale(1)',
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }} 
                    onClick={() => toggleDay(index)}
                    onMouseEnter={(e) => {
                      if (dayStatus !== 'upcoming') {
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(175, 122, 44, 0.15)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = dayStatus === 'current' 
                        ? '0 4px 20px rgba(175, 122, 44, 0.15)' 
                        : '0 2px 8px rgba(0,0,0,0.04)';
                      e.currentTarget.style.transform = 'translateX(0px)';
                    }}
                  >
                    {/* Gold accent line for current day */}
                    {dayStatus === 'current' && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '60px',
                        height: '60px',
                        background: 'radial-gradient(circle, rgba(175, 122, 44, 0.08) 0%, transparent 70%)',
                        pointerEvents: 'none'
                      }}></div>
                    )}
                    
                    <div style={dayHeaderStyle}>
                      <div style={{ flex: 1 }}>
                        <div style={dayTitleStyle}>
                          <span style={{ 
                            fontWeight: '700', 
                            fontSize: '16px',
                            color: dayStatus === 'current' ? '#AF7A2C' : colors.titleColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span>{colors.icon}</span>
                            Day {day.day || index + 1}
                          </span>
                          {day.date && (
                            <span style={{ 
                              marginLeft: '12px', 
                              color: '#666', 
                              fontSize: '14px',
                              fontWeight: '400'
                            }}>
                              {day.date}
                            </span>
                          )}
                          {/* Status Badge with AF7A2C Gold Theme */}
                          <span style={{
                            marginLeft: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: dayStatus === 'current' ? '#AF7A2C' : colors.statusColor,
                            backgroundColor: dayStatus === 'current' ? '#FFF8E1' : colors.statusBg,
                            padding: '3px 12px',
                            borderRadius: '12px',
                            border: `1px solid ${dayStatus === 'current' ? '#AF7A2C44' : colors.statusColor + '33'}`,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                          }}>
                            {colors.statusText}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: dayStatus === 'upcoming' ? '#9E9E9E' : '#333',
                          fontWeight: '500',
                          marginTop: '4px'
                        }}>
                          {day.title}
                        </div>
                      </div>
                      <div style={{
                        transform: expandedDay === index ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        color: dayStatus === 'current' ? '#AF7A2C' : colors.titleColor,
                        fontSize: '20px',
                        opacity: 0.6
                      }}>
                        ▼
                      </div>
                    </div>

                    {/* Expanded Description */}
                    <div style={{
                      maxHeight: expandedDay === index ? '1000px' : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      <div style={{
                        ...dayDescriptionStyle,
                        borderTopColor: dayStatus === 'current' ? '#AF7A2C44' : colors.borderLeftColor + '44'
                      }}>
                        <div 
                          dangerouslySetInnerHTML={{ __html: day.description }}
                          style={{ 
                            fontSize: '14px',
                            lineHeight: '1.8',
                            color: dayStatus === 'upcoming' ? '#999' : '#444'
                          }}
                        />
                      </div>
                    </div>

                    {/* Status Indicator */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: dayStatus === 'current' 
                          ? `radial-gradient(circle at 35% 35%, ${'#AF7A2C'}, ${'#8B6914'})`
                          : `radial-gradient(circle at 35% 35%, ${colors.dotBg}, ${colors.dotBg}dd)`,
                        animation: dayStatus === 'current' ? 'pulse-gold 1.5s ease-in-out infinite' : 'none'
                      }}></span>
                      <span style={{
                        fontSize: '12px',
                        color: dayStatus === 'current' ? '#AF7A2C' : colors.statusColor,
                        fontWeight: '500'
                      }}>
                        {expandedDay === index ? '📖 Viewing details' : '👆 Click to expand'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with AF7A2C Gold Theme */}
        <div style={{
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: `2px solid ${'#AF7A2C'}44`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <span style={{ fontSize: '13px', color: '#8B6914' }}>
              🟢 Completed &nbsp; 🟠 Today &nbsp; ⚪ Upcoming
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 28px',
              background: `linear-gradient(135deg, ${'#AF7A2C'} 0%, ${'#C8963E'} 50%, ${'#D4A84E'} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 16px rgba(175, 122, 44, 0.3)',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 6px 24px rgba(175, 122, 44, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 16px rgba(175, 122, 44, 0.3)';
            }}
          >
            ✨ Close Itinerary
          </button>
        </div>

        {/* CSS Animation Keyframes */}
        <style>{`
          @keyframes pulse-gold {
            0% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 0 4px rgba(175, 122, 44, 0.3);
            }
            50% { 
              transform: scale(1.15); 
              opacity: 0.9;
              box-shadow: 0 0 0 12px rgba(175, 122, 44, 0.1);
            }
            100% { 
              transform: scale(1); 
              opacity: 1;
              box-shadow: 0 0 0 4px rgba(175, 122, 44, 0.3);
            }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          /* Custom scrollbar with AF7A2C color */
          .modal-scroll::-webkit-scrollbar {
            width: 6px;
          }
          
          .modal-scroll::-webkit-scrollbar-track {
            background: #f5f5f5;
            border-radius: 8px;
          }
          
          .modal-scroll::-webkit-scrollbar-thumb {
            background: #AF7A2C;
            border-radius: 8px;
          }
          
          .modal-scroll::-webkit-scrollbar-thumb:hover {
            background: #8B6914;
          }
        `}</style>
      </div>
    </div>
  );
};

// Inline styles with AF7A2C Gold Theme
const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10000,
  padding: '20px',
  animation: 'fadeIn 0.3s ease'
};

const modalStyle = {
  background: '#fff',
  borderRadius: '16px',
  padding: '32px 28px',
  width: '100%',
  maxWidth: '820px',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  scrollbarWidth: 'thin',
  scrollbarColor: '#AF7A2C #f5f5f5',
};

const closeBtnStyle = {
  position: 'absolute',
  top: '16px',
  right: '20px',
  background: 'rgba(255,255,255,0.2)',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: '#fff',
  transition: 'all 0.3s ease',
  padding: '4px 8px',
  borderRadius: '4px',
  zIndex: 10,
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
};

const modalTitleStyle = {
  display: 'flex',
  alignItems: 'center',
};

const badgeStyle = {
  padding: '6px 16px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: '500',
};

const timelineContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const timelineItemStyle = {
  display: 'flex',
  gap: '16px',
  position: 'relative',
};

const timelineLeftStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '4px',
  width: '36px',
  flexShrink: 0,
};

const timelineLineContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  minHeight: '60px',
  position: 'relative',
  width: '100%',
};

const timelineLineStyle = {
  flex: 1,
  width: '3px',
  minHeight: '20px',
  transition: 'all 0.3s ease',
  borderRadius: '2px',
};

const timelineDotStyle = {
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  zIndex: 1,
};

const timelineContentStyle = {
  flex: 1,
  paddingBottom: '4px',
};

const dayCardStyle = {
  borderRadius: '12px',
  padding: '18px 22px',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  border: '1px solid #f0f0f0',
  marginBottom: '4px',
  position: 'relative',
  overflow: 'hidden',
};

const dayHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
};

const dayTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
};

const dayDescriptionStyle = {
  paddingTop: '16px',
  marginTop: '12px',
  borderTop: '2px solid #f0f0f0',
};

// Main Component
function Upcoming_tour_content() {
  const [upcomingTours, setUpcomingTours] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedItineraryBooking, setSelectedItineraryBooking] = useState(null);
  const accessToken = localStorage.getItem('accessToken');
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [loadingCancel, setLoadingCancel] = useState(false);

  useEffect(() => {
    const fetchUpcomingTours = async () => {
      try {
        const response = await api.get('/user/bookings/paid', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        if (response.data.success === true) {
          setUpcomingTours(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching upcoming tours:', error);
      }
    };
    fetchUpcomingTours();
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  const upCommingTours = upcomingTours.filter((tour) =>
    dayjs(tour?.tour_end_date).isAfter(dayjs())
  );

  const totalPages = Math.ceil(upCommingTours.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTours = upCommingTours.slice(indexOfFirstItem, indexOfLastItem);

  const formatDate = (date) => dayjs(date).format("D MMM h:mm a");
  const handlepdf = (bookingId) => {
    window.open(`https://admin.jayrajgroup.com/admin/tour_bookings/${bookingId}/invoice`, '_blank');
  };

  const handleCancel = (id) => {
    setCancelBookingId(id);
    setCancelModal(true);
  };

  const confirmCancelBooking = async () => {
    try {
      setLoadingCancel(true);
      const response = await api.post(
        `/tour-bookings/${cancelBookingId}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (response.data.success) {
        setUpcomingTours((prevTours) =>
          prevTours.map((tour) =>
            tour.id === cancelBookingId
              ? { ...tour, status: "cancelled" }
              : tour
          )
        );
        setCancelModal(false);
        alert("Booking cancelled successfully");
      }
    } catch (error) {
      console.error("Cancel booking error:", error);
      alert("Failed to cancel booking");
    } finally {
      setLoadingCancel(false);
    }
  };

  return (
    <div className="upcoming_tour_content">
      {currentTours.map((tour) => (
        <div className="tripCard_wrapper" key={tour.id}>
          <div className="tripCard_image">
            <img src={tour?.tour?.destination?.image} alt={tour?.tour?.title} />
          </div>
          <div className="tripCard_content">
            <h2 className="tripCard_title">{tour?.tour?.title}</h2>
            <p className="tripCard_date" style={{ margin: 1 }}>
              {`${formatDate(tour?.tour_start_date)} - ${formatDate(tour?.tour_end_date)}`}
            </p>
            <p className="tripCard_date" style={{ margin: 4, fontWeight: "600" }}>
              Paid Amount: ₹{tour?.paid_amount}
            </p>
            <div className="tripCard_buttons">
              <button
                className="tripCard_btnDetails"
                onClick={() => handlepdf(tour.id)}
              >
                <i className="fas fa-download" style={{ marginRight: "6px" }}></i>
                Invoice
              </button>

              {/* Track Itinerary Button with AF7A2C Gold Theme */}
              <button
                className="tripCard_btnItinerary"
                onClick={() => setSelectedItineraryBooking(tour)}
                style={{
                  background: `linear-gradient(135deg, #AF7A2C 0%, #C8963E 50%, #D4A84E 100%)`,
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(175, 122, 44, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 16px rgba(175, 122, 44, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 2px 8px rgba(175, 122, 44, 0.3)';
                }}
              >
                <span style={{ fontSize: '16px' }}>📍</span>
                Track Itinerary
              </button>

              <button
                className="tripCard_btnCancel"
                onClick={() => handleCancel(tour.id)}
                disabled={tour?.status === "cancelled"}
              >
                {tour?.status === "cancelled" ? "Cancelled" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "30px",
          gap: "10px"
        }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination_btn"
          >
            ◀ Prev
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index + 1)}
              className={`pagination_btn ${currentPage === index + 1 ? "active_page" : ""}`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination_btn"
          >
            Next ▶
          </button>
        </div>
      )}

      {/* Itinerary Modal */}
      {selectedItineraryBooking && (
        <ItineraryModal
          booking={selectedItineraryBooking}
          onClose={() => setSelectedItineraryBooking(null)}
        />
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: "500px", textAlign: "center" }}>
            <h2 style={{ marginBottom: "15px", color: "#e53935" }}>
              Cancel Booking
            </h2>
            <p style={{ marginBottom: "10px" }}>
              If you cancel this tour booking, cancellation charges may apply.
            </p>
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "20px" }}>
              Please review the cancellation policy before confirming.
              Refund amount will be processed according to the policy.
            </p>
            <ul style={{ textAlign: "left", marginBottom: "20px" }}>
              <li>Cancellation may deduct service charges.</li>
              <li>Refund will be credited within 5-7 working days.</li>
              <li>This action cannot be undone.</li>
            </ul>
            <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
              <button
                onClick={confirmCancelBooking}
                disabled={loadingCancel}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#e53935",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                {loadingCancel ? "Cancelling..." : "Confirm Cancel"}
              </button>
              <button
                onClick={() => setCancelModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                No, Keep This
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Upcoming_tour_content;